import { useState, useMemo, useCallback } from 'react';
import { useLoadMorePagination } from '../../../hooks/useLoadMorePagination';

/**
 * Hook for filtering, sorting and pagination of folders and documents
 * @param {Object} options - Hook options
 * @param {Array} options.folders - Array of folders to filter/sort
 * @param {Array} options.documents - Array of documents to filter/sort
 * @param {Object} options.storeRef - Ref to the workspace store
 * @param {boolean} options.foldersHasMore - If backend has more folders
 * @param {boolean} options.isLoadingMoreFolders - If loading more folders
 * @param {boolean} options.documentsHasMore - If backend has more documents
 * @param {boolean} options.isLoadingMoreDocuments - If loading more documents
 * @returns {Object} - Filter/sort state and pagination results
 */
export const useFilterAndSort = ({
  folders,
  documents,
  storeRef,
  foldersHasMore,
  isLoadingMoreFolders,
  documentsHasMore,
  isLoadingMoreDocuments,
}) => {
  // Filter and sort state
  const [filterText, setFilterText] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');

  // Filter and sort folders with useMemo
  const filteredAndSortedFolders = useMemo(() => {
    let filtered = folders;

    // Filter if there's search text
    if (filterText) {
      filtered = filtered.filter((folder) =>
        folder.name.toLowerCase().includes(filterText.toLowerCase())
      );

      // Only sort if filter is applied
      return filtered.slice().sort((a, b) => {
        const direction = sortDirection === 'asc' ? 1 : -1;

        switch (sortBy) {
          case 'name':
            return direction * a.name.localeCompare(b.name);
          case 'lastUpdated':
            return direction * (new Date(a.updated_at) - new Date(b.updated_at));
          default:
            return 0;
        }
      });
    }

    // If no filter, don't sort - keep backend order
    return filtered;
  }, [folders, filterText, sortBy, sortDirection]);

  // Filter and sort documents with useMemo
  const filteredAndSortedDocuments = useMemo(() => {
    let filtered = documents;

    // Filter if there's search text
    if (filterText) {
      filtered = filtered.filter((doc) =>
        doc.name.toLowerCase().includes(filterText.toLowerCase())
      );

      // Only sort if filter is applied
      return filtered.slice().sort((a, b) => {
        const direction = sortDirection === 'asc' ? 1 : -1;

        switch (sortBy) {
          case 'name':
            return direction * a.name.localeCompare(b.name);
          case 'lastUpdated':
            return direction * (new Date(a.updated_at) - new Date(b.updated_at));
          default:
            return 0;
        }
      });
    }

    // If no filter, don't sort - keep backend order
    return filtered;
  }, [documents, filterText, sortBy, sortDirection]);

  // Memoize loadMore callbacks to avoid recreating on every render
  const loadMoreFoldersCallback = useCallback(() => {
    storeRef.current.loadMoreFolders();
  }, [storeRef]);

  const loadMoreDocumentsCallback = useCallback(() => {
    storeRef.current.loadMoreDocuments();
  }, [storeRef]);

  // Pagination hooks with backend support
  const {
    visibleItems: visibleFolders,
    hasMore: hasMoreFolders,
    isLoading: isLoadingFolders,
    loadMore: loadMoreFolders,
  } = useLoadMorePagination(
    filteredAndSortedFolders,
    8,
    2000,
    foldersHasMore,
    loadMoreFoldersCallback,
    isLoadingMoreFolders
  );

  const {
    visibleItems: visibleDocuments,
    hasMore: hasMoreDocuments,
    isLoading: isLoadingDocuments,
    loadMore: loadMoreDocuments,
  } = useLoadMorePagination(
    filteredAndSortedDocuments,
    8,
    2000,
    documentsHasMore,
    loadMoreDocumentsCallback,
    isLoadingMoreDocuments
  );

  // Handle sort change
  const handleSortChange = useCallback(
    (field) => {
      if (sortBy === field) {
        setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortBy(field);
        setSortDirection('asc');
      }
    },
    [sortBy]
  );

  return {
    // Filter/sort state
    filterText,
    setFilterText,
    sortBy,
    sortDirection,
    handleSortChange,

    // Pagination results - folders
    visibleFolders,
    hasMoreFolders,
    isLoadingFolders,
    loadMoreFolders,

    // Pagination results - documents
    visibleDocuments,
    hasMoreDocuments,
    isLoadingDocuments,
    loadMoreDocuments,
  };
};

export default useFilterAndSort;
